/**
 * 룸 연결(C-01 · IF-06) — 전송(Colyseus)과 무관한 상태 기계 + 시계 동기 + 스냅샷 보간.
 *  - 입장: 제어 평면 조인 토큰(GET …/room) → connect → FullState(SR-NET-04)
 *  - 시계: Ping 4번 중 RTT 가 가장 짧은 표본으로 서버 틱 추정 · 스냅샷 tick 으로 드리프트 보정
 *  - IF-06 v2: FullState + 기계 엔티티 델타를 EntityMirror 로 복원 · 받은 틱을 ACK(다음 델타의 기준 · SR-NET-02)
 *  - 보간: 스냅샷 2개(100 ms · 열화 ④ 10 Hz 면 200 ms) 뒤를 그린다 — 위치 선형 · 회전 정규화 선형(ARC §9.3)
 *  - 예측(SR-NET-03): 내 기계는 로컬 커널(Predictor)이 입력을 곧바로 반영 · 서버와 5 cm 넘게 어긋나면 되감아 0.2초에 걸쳐 맞춘다
 *  - 끊김(ARC §9.4 · SR-LCL-04): 5초마다 새 토큰으로 다시 · 20초 넘으면 failed(FE-303 → 혼자 놀이터)
 *  - 닫힘: 4000 = 룸 종료(reason teacher · idle — P2-S4 SR-ROOM-04) · 4001 = 다른 창이 이어받음 · 4010 = 서버 종료(다시 붙는다)
 *    · 입장 거부 FE-301/302/304
 *  - P2-S4 교사 개입(SR-SEC-03): 일시정지(meta.paused · 스냅샷 PAUSED 깃발 · ev paused) · 분리(me.isolated · ev isolated)
 *    · 룸 타이머(meta.timer · ev timer/timerEnd) · 기계 리셋(ev machineReset) — 상태는 여기서 모아 화면에 준다
 *  - P2-S5 마무리: 스크립트(meta.script · ev script — 남은 초는 받은 시각부터 여기서 센다) · 막힌 포트(ev stall · meta.stalls 선택)
 *    · 칠판 하이라이트(ev highlight · meta.highlight 선택 — 칠판 세션만) · 허브 빛·소리(ev hub — 칠판이 그린다)
 */
import {
  decodePong,
  encodeAck,
  encodeInput,
  encodePing,
  EntityMirror,
  MSG,
  SNAP_HALF_RATE,
  SNAP_PAUSED,
} from "@picnic-field/room-protocol";
import type { RemoteInput } from "@picnic-field/local-host/protocol";
import type { PrincipleMachineLike } from "@picnic-field/renderer/principle";
import { velocityOf, type Predictor } from "./predict";
import { readScript } from "./script";
import { EMPTY_MODE_STATE, modeReduce, type ModeState } from "./modeState";
import type {
  RoomTicket,
  RoomTransport,
  Connect,
  RejectCode,
  ConnState,
  RoomMember,
  RoomBody,
  RoomModeMeta,
  RoomEvent,
  RoomTimer,
  RoomMeta,
  ScriptState,
} from "../types";

export const TICK_MS = 1000 / 60;

/** P3-S2 릴레이 학생 명령 메시지 이름(room-protocol MSG.relay = "rl") */
export const RELAY_MSG = MSG.relay;
export const RECONNECT_WINDOW_MS = 20_000;
export const RECONNECT_EVERY_MS = 5_000;
/** 보간 지연 — 스냅샷 2개(3틱 × 2) */
export const INTERP_TICKS = 6;
const PINGS = 4;

interface WireMeta {
  roomId: string;
  mode: RoomModeMeta;
  me: { machine: number | null; role: string; isolated?: boolean; drive?: "program" | "remote" };
  paused: boolean;
  timer?: RoomTimer | null;
  script?: unknown;
  highlight?: number | null;
  stalls?: Record<string, readonly string[]> | null;
  members: RoomMember[];
  bodyCount: number;
  machines: {
    machine: number;
    snapshotId: string;
    bodyStart: number;
    bodyCount: number;
    template: number;
    glbUrl: string | null;
    appearance: unknown;
    /** P2-S3 운반 물체(엔티티 200+) — 조각 id */
    object?: string;
  }[];
  templates: Omit<RoomBody, "index" | "owner">[][];
  env: RoomBody[];
}

export const ownerOf = (machine: number) => `m${machine}`;

/** 틀로 묶어 온 몸체 설명을 펼친다(owner = m<번호>) */
export function expandMeta(w: WireMeta): RoomMeta {
  const bodies: RoomBody[] = new Array(w.bodyCount);
  for (const b of w.env) bodies[b.index] = b;
  for (const m of w.machines) {
    const tpl = w.templates[m.template] ?? [];
    for (let k = 0; k < m.bodyCount; k++)
      bodies[m.bodyStart + k] = {
        ...(tpl[k] ?? { kind: "sub" }),
        index: m.bodyStart + k,
        // 운반 물체는 환경처럼 그린다(렌더러 인스턴스 — 매 프레임 자세)
        owner: m.object ? "env" : ownerOf(m.machine),
      };
  }
  for (let i = 0; i < bodies.length; i++) bodies[i] ??= { index: i, kind: "env", owner: "env" };
  return {
    roomId: w.roomId,
    mode: w.mode,
    me: w.me,
    paused: w.paused,
    timer: w.timer ?? null,
    script: readScript(w.script),
    highlight: typeof w.highlight === "number" ? w.highlight : null,
    stalls: w.stalls && typeof w.stalls === "object" ? w.stalls : null,
    members: w.members,
    machines: w.machines
      .filter((m) => !m.object)
      .map((m) => ({
        machine: m.machine,
        owner: ownerOf(m.machine),
        snapshotId: m.snapshotId,
        bodyStart: m.bodyStart,
        bodyCount: m.bodyCount,
        glbUrl: m.glbUrl,
        appearance: m.appearance,
      })),
    bodies,
  };
}

/** a→b 사이 u 로 자세 보간(out 에) */
export function lerpTransforms(
  a: Float32Array,
  b: Float32Array,
  u: number,
  out: Float32Array,
): void {
  const n = Math.min(a.length, b.length, out.length);
  for (let o = 0; o + 7 <= n; o += 7) {
    out[o] = a[o]! + (b[o]! - a[o]!) * u;
    out[o + 1] = a[o + 1]! + (b[o + 1]! - a[o + 1]!) * u;
    out[o + 2] = a[o + 2]! + (b[o + 2]! - a[o + 2]!) * u;
    // 짧은 쪽으로(부호 맞춤) 정규화 선형
    const dot =
      a[o + 3]! * b[o + 3]! + a[o + 4]! * b[o + 4]! + a[o + 5]! * b[o + 5]! + a[o + 6]! * b[o + 6]!;
    const s = dot < 0 ? -1 : 1;
    let x = a[o + 3]! + (s * b[o + 3]! - a[o + 3]!) * u;
    let y = a[o + 4]! + (s * b[o + 4]! - a[o + 4]!) * u;
    let z = a[o + 5]! + (s * b[o + 5]! - a[o + 5]!) * u;
    let w = a[o + 6]! + (s * b[o + 6]! - a[o + 6]!) * u;
    const len = Math.hypot(x, y, z, w) || 1;
    x /= len;
    y /= len;
    z /= len;
    w /= len;
    out[o + 3] = x;
    out[o + 4] = y;
    out[o + 5] = z;
    out[o + 6] = w;
  }
}

export interface ConnectionDeps {
  /** 새 조인 토큰(룸이 없으면 null — 닫힘) · 거부는 ApiError(FE-301 등)로 던진다 */
  readonly ticket: () => Promise<RoomTicket | null>;
  readonly connect: Connect;
  readonly now: () => number;
  readonly setTimeout: (cb: () => void, ms: number) => unknown;
  readonly clearTimeout: (h: unknown) => void;
  /** 내 기계 예측 월드(없으면 보간만) — 예측할 수 있는 모드({@link predictable})의 FullState 를 받으면 만든다(모드가 바뀌면 버린다) */
  readonly predictor?: (meta: RoomMeta) => Promise<Predictor | null>;
}

/**
 * 예측 월드(predictWorld.ts — 내 기계 한 대 + 전시관 hall 바닥)가 서버와 같은 바닥을 만드는 모드인가 — 로비뿐.
 * 룸 모드(밀어내기 링 · 협동 · 릴레이 · 자유 놀이터)는 서버가 배치(링 윗면 0.1 m · 레인 · 물체 · 상대 기계)를 따로 만들어
 * 로컬 월드에 없다 — 예측하면 내 기계가 링 아래 바닥으로 가라앉고(0.155 → 0.055 m) 5 cm 되감기를 되풀이한다. 그런 모드는 보간만.
 */
export function predictable(mode: { readonly id: string; readonly presetKey: string }): boolean {
  return mode.id === "gallery" && mode.presetKey === "hall";
}
const modeKey = (m: RoomMeta) => `${m.mode.id}|${m.mode.presetKey}`;

const rejectOf = (e: unknown): RejectCode | null => {
  const s = `${(e as { code?: unknown })?.code ?? ""} ${(e as Error)?.message ?? ""}`;
  const m = /FE-30[124]/.exec(s);
  return m ? (m[0] as RejectCode) : null;
};

export class RoomConnection {
  state: ConnState = { kind: "connecting" };
  meta: RoomMeta | null = null;
  members: readonly RoomMember[] = [];
  /** P2-S4 선생님 개입 상태 — 일시정지 · 나는 관전(분리) · 룸 타이머 끝 시각(now() 기준 ms) */
  paused = false;
  isolated = false;
  /** 리모컨 모드(2026-10-11) — null = 이 모드에는 없음(로비 · 자유 놀이터) */
  drive: "program" | "remote" | null = null;
  timerEndsAt: number | null = null;
  /** P2-S5 마무리 스크립트 · 받은 시각(now() ms — 남은 초를 여기서 센다) */
  script: ScriptState | null = null;
  scriptAt = 0;
  /** P2-S5 막힌 포트(기계 → 포트 · 빈 배열이면 뺀다) — 승패 아님 */
  readonly stalls = new Map<number, readonly string[]>();
  /** P2-S5 칠판 하이라이트 기계(칠판 세션만 받는다) */
  highlight: number | null = null;
  /** P2-S5 허브 빛(기계 → 색) · 마지막 소리 시각(기계 → now() ms) */
  readonly hubLight = new Map<number, string>();
  readonly hubBeepAt = new Map<number, number>();
  /**
   * 모드 이벤트 상태(밀어내기 match·result·queue·matchEnd · 협동 teamComplete·teamEnd) — 받는 즉시 접는다.
   * 모드 화면은 늦게 구독하므로(새 모드 FullState → 그린 뒤 구독) 마운트할 때 여기서 시작한다 · 새 FullState 마다 비운다.
   */
  modeState: ModeState = EMPTY_MODE_STATE;
  /** 받은 상태(FullState + 델타) */
  private mirror: EntityMirror<WireMeta> | null = null;
  private history: { tick: number; t: Float32Array; at: number }[] = [];
  private interpTicks = INTERP_TICKS;
  private predictor: Predictor | null = null;
  /** 예측 월드를 만든 모드(modeKey) — 새 FullState 의 모드가 다르면 버린다 */
  private predictorMode: string | null = null;
  private predictorLoading = false;
  private transport: RoomTransport | null = null;
  private timer: unknown = null;
  private disposed = false;
  private readonly listeners = new Set<() => void>();
  private readonly eventListeners = new Set<(e: RoomEvent) => void>();
  /** 서버 틱 ≈ now/TICK_MS + offset */
  private offset: number | null = null;
  private bestRtt = Infinity;
  private pings = 0;
  private generation = 0;
  private readonly out = { frame: new Float32Array(0) };
  lastInputTick = 0;
  /**
   * 원리 보기(SR-PHY-07 · 2026-09-28 룸 화면) — 켠 요청(재접속 · 새 FullState 뒤 다시 보낸다 — 서버는 연결마다 끈다) ·
   * 받은 값(서버 권위 월드 · 틱 순 · 최근 몇 개 — 보간 틱에 맞는 것을 고른다)
   */
  private principleWant: { on: true; machine?: number } | null = null;
  private principleRx: { tick: number; machine: number; machines: PrincipleMachineLike[] }[] = [];

  constructor(private readonly d: ConnectionDeps) {}

  /** 리모컨 모드 켜기 · 끄기(MSG.drive · 2026-10-11) — 서버가 ev drive 로 확인한다(그때 drive 가 바뀐다) */
  sendDrive(remote: boolean): void {
    if (this.state.kind === "live") this.transport?.sendJson?.(MSG.drive, { remote });
  }

  /**
   * 원리 보기 켜기 · 끄기(MSG.principle) — 학생은 서버가 제 기계로 정한다 · 교사 · 칠판은 machine. 켜 두면 재접속 뒤에도 다시 건다.
   */
  sendPrinciple(on: boolean, machine?: number | null): void {
    const was = this.principleWant?.machine;
    this.principleWant = on ? { on: true, ...(machine != null ? { machine } : {}) } : null;
    // 끄거나 다른 기계로 바꾸면(교사 · 칠판) 앞 기계 값을 버린다 — 새 기계 자리에 옛 화살표가 남지 않게
    if (!on || was !== this.principleWant?.machine) this.principleRx = [];
    if (this.state.kind === "live")
      this.transport?.sendJson?.(MSG.principle, this.principleWant ?? { on: false });
  }

  /** 이 보간 틱에 맞는 원리 값(없으면 null) — 그 틱 뒤 가장 가까운 것 · 없으면 가장 옛것 */
  private principleAt(target: number) {
    const rx = this.principleRx;
    if (!this.principleWant || !rx.length) return null;
    let pick = rx[0]!;
    for (const r of rx) if (r.tick <= target) pick = r;
    return pick;
  }

  /** 모드·이모트 이벤트(당사자에게만 오는 결과 포함) */
  onEvent(cb: (e: RoomEvent) => void): () => void {
    this.eventListeners.add(cb);
    return () => this.eventListeners.delete(cb);
  }

  /** 이모트·정형 문구(카탈로그 키 — 서버가 초당 1회로 거른다) */
  sendEmote(key: string): void {
    if (this.state.kind === "live") this.transport?.sendJson?.(MSG.emote, { key });
  }

  /**
   * P3-S2 릴레이 학생 명령(계약 §1 · MSG.relay = "rl") — ready/unready(Setup '✓ 다 됐어' 토글) · retry(내 구간 Failed 만).
   * 거절은 그 학생에게만 ev relayRejected { op, reason }. 보냈으면 true.
   */
  sendRelay(op: "ready" | "unready" | "retry"): boolean {
    if (this.state.kind !== "live" || !this.transport?.sendJson) return false;
    this.transport.sendJson(RELAY_MSG, { op });
    return true;
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
  private emit(): void {
    for (const cb of this.listeners) cb();
  }
  private set(s: ConnState): void {
    this.state = s;
    this.emit();
  }

  start(): void {
    void this.attempt();
  }

  private async attempt(): Promise<void> {
    const gen = ++this.generation;
    let ticket: RoomTicket | null;
    try {
      ticket = await this.d.ticket();
    } catch (e) {
      if (gen !== this.generation || this.disposed) return;
      const code = rejectOf(e);
      if (code) return this.set({ kind: "rejected", code });
      return this.retryLater();
    }
    if (gen !== this.generation || this.disposed) return;
    if (!ticket) return this.set({ kind: "closed" });
    let t: RoomTransport;
    try {
      t = await this.d.connect(ticket);
    } catch (e) {
      if (gen !== this.generation || this.disposed) return;
      const code = rejectOf(e);
      if (code) return this.set({ kind: "rejected", code });
      return this.retryLater();
    }
    if (gen !== this.generation || this.disposed) {
      void t.leave();
      return;
    }
    this.bind(t);
  }

  private retryLater(): void {
    const now = this.d.now();
    const deadline =
      this.state.kind === "reconnecting" ? this.state.deadline : now + RECONNECT_WINDOW_MS;
    if (now >= deadline) return this.set({ kind: "failed", code: "FE-303" });
    const attempts = this.state.kind === "reconnecting" ? this.state.attempts + 1 : 1;
    this.set({ kind: "reconnecting", deadline, attempts });
    this.timer = this.d.setTimeout(
      () => void this.attempt(),
      Math.min(RECONNECT_EVERY_MS, Math.max(0, deadline - now)),
    );
  }

  private bind(t: RoomTransport): void {
    this.transport = t;
    t.onBytes(MSG.fullState, (b) => {
      const fs = EntityMirror.fromFullState<WireMeta>(b);
      if (!fs) return;
      this.mirror = fs.mirror;
      this.meta = expandMeta(fs.mirror.meta);
      this.modeState = EMPTY_MODE_STATE;
      this.members = this.meta.members;
      this.paused = !!this.meta.paused;
      this.isolated = !!this.meta.me.isolated;
      this.drive = this.meta.me.drive ?? null;
      this.setTimer(this.meta.timer ?? null);
      this.setScript(this.meta.script ?? null);
      if (typeof this.meta.highlight === "number") this.highlight = this.meta.highlight;
      if (this.meta.stalls) {
        this.stalls.clear();
        for (const [k, v] of Object.entries(this.meta.stalls))
          if (Array.isArray(v) && v.length) this.stalls.set(Number(k), v);
      }
      const tr = fs.mirror.transforms(fs.tick)!;
      this.history = [{ tick: fs.tick, t: tr, at: this.d.now() }];
      this.out.frame = new Float32Array(tr.length);
      if (this.offset === null) this.offset = fs.tick - this.d.now() / TICK_MS;
      this.state = { kind: "live" };
      this.syncClock();
      // 모드가 바뀌었으면 예측 월드를 버린다(로컬 바닥이 서버와 다르다) · 재접속이면 서버 자세에서 다시
      if (this.predictor && this.predictorMode !== modeKey(this.meta)) this.dropPredictor();
      if (this.predictor) this.predictor.sync(fs.tick, this.mine(tr)!);
      else this.loadPredictor();
      // 원리 보기 — 서버는 연결마다 끈다(새 연결이면 다시 건다)
      if (this.principleWant) t.sendJson?.(MSG.principle, this.principleWant);
      this.emit();
    });
    t.onBytes(MSG.snapshot, (b) => {
      const m = this.mirror;
      if (!m) return;
      const s = m.apply(b);
      if (!s) return;
      // 받은 틱 — 다음 델타의 기준(SR-NET-02)
      t.sendBytes(MSG.ack, encodeAck(s.tick));
      this.interpTicks = s.flags & SNAP_HALF_RATE ? INTERP_TICKS * 2 : INTERP_TICKS;
      // 일시정지 깃발(이벤트를 놓쳐도 스냅샷이 알려 준다)
      const paused = (s.flags & SNAP_PAUSED) !== 0;
      if (paused !== this.paused) {
        this.paused = paused;
        this.emit();
      }
      const tr = m.transforms(s.tick)!;
      const prev = this.history[this.history.length - 1];
      this.history.push({ tick: s.tick, t: tr, at: this.d.now() });
      if (this.history.length > 8) this.history.shift();
      // 드리프트: 스냅샷이 추정보다 앞서 오면 시계를 당긴다
      const est = this.serverTick();
      if (est !== null && s.tick > est + 3) this.offset! += s.tick - est - 1;
      // 예측 재조정 — 서버가 본 내 기계(틱 s.tick)
      if (this.predictor) {
        const mine = this.mine(tr)!;
        const vel = prev
          ? velocityOf(this.mine(prev.t)!, mine, ((s.tick - prev.tick) * TICK_MS) / 1000)
          : null;
        this.predictor.reconcile(s.tick, mine, vel);
      }
    });
    t.onBytes(MSG.pong, (b) => {
      const p = decodePong(b);
      if (!p) return;
      const now = this.d.now();
      const rtt = now - p.clientMs;
      if (rtt < this.bestRtt) {
        this.bestRtt = rtt;
        const serverNow = p.serverTick + (p.msIntoTick + rtt / 2) / TICK_MS;
        this.offset = serverNow - now / TICK_MS;
      }
      if (++this.pings < PINGS) this.timer = this.d.setTimeout(() => this.ping(), 100);
    });
    t.onJson(MSG.members, (body) => {
      const m = (body as { members?: RoomMember[] })?.members;
      if (Array.isArray(m)) {
        this.members = m;
        this.emit();
      }
    });
    t.onJson(MSG.event, (body) => {
      const e = body as RoomEvent | null;
      if (!e || typeof e.t !== "string") return;
      this.applyEvent(e);
      for (const cb of this.eventListeners) cb(e);
    });
    t.onJson(MSG.principleState, (body) => {
      const b = body as { tick?: unknown; machine?: unknown; machines?: unknown } | null;
      if (!this.principleWant || !b || typeof b.tick !== "number" || !Array.isArray(b.machines))
        return;
      // 고른 기계가 아닌 값(바꾸기 전에 서버가 보낸 것)은 버린다
      const want = this.principleWant.machine;
      if (want !== undefined && typeof b.machine === "number" && b.machine !== want) return;
      const rx = this.principleRx;
      if (rx.length && rx[rx.length - 1]!.tick >= b.tick) rx.length = 0;
      rx.push({
        tick: b.tick,
        machine: typeof b.machine === "number" ? b.machine : -1,
        machines: b.machines as PrincipleMachineLike[],
      });
      if (rx.length > 4) rx.shift();
    });
    t.onJson(MSG.error, () => {});
    t.onLeave((code, reason) => this.onLeave(t, code, reason));
  }

  /** P2-S4 교사 개입 이벤트 → 상태(화면은 onChange 로 다시 그린다) */
  private applyEvent(e: RoomEvent): void {
    const mode = modeReduce(this.modeState, e, this.meta);
    if (mode) {
      this.modeState = mode;
      return this.emit();
    }
    if (e.t === "paused") this.paused = e.on === true;
    else if (e.t === "isolated") this.isolated = e.on === true;
    else if (e.t === "drive") this.drive = e.remote === true ? "remote" : "program";
    else if (e.t === "timer")
      this.setTimer(typeof e.endsInS === "number" ? { endsInS: e.endsInS } : null);
    else if (e.t === "timerEnd") this.timerEndsAt = null;
    else if (e.t === "script") this.setScript(readScript(e.script));
    else if (e.t === "highlight") this.highlight = typeof e.machine === "number" ? e.machine : null;
    else if (e.t === "stall" && typeof e.machine === "number") {
      const ports = Array.isArray(e.ports)
        ? e.ports.filter((p): p is string => typeof p === "string")
        : [];
      if (ports.length) this.stalls.set(e.machine, ports);
      else this.stalls.delete(e.machine);
    } else if (e.t === "hub" && typeof e.machine === "number") {
      if (typeof e.light === "string") {
        if (e.light && e.light !== "off" && e.light !== "black")
          this.hubLight.set(e.machine, e.light);
        else this.hubLight.delete(e.machine);
      }
      if (e.beep === true) this.hubBeepAt.set(e.machine, this.d.now());
    } else return;
    this.emit();
  }

  private setScript(s: ScriptState | null): void {
    this.script = s;
    this.scriptAt = this.d.now();
  }

  /** 스크립트 지금 단계 남은 초(0 아래로는 0 — 넘김은 교사) · 스크립트가 없으면 null */
  scriptLeftS(): number | null {
    const s = this.script;
    if (!s || s.done) return null;
    const gone = s.running ? (this.d.now() - this.scriptAt) / 1000 : 0;
    return Math.max(0, Math.ceil(s.remainingS - gone));
  }

  private setTimer(t: RoomTimer | null): void {
    this.timerEndsAt = t && t.endsInS > 0 ? this.d.now() + t.endsInS * 1000 : null;
  }

  /** 룸 타이머 남은 초(없으면 null) */
  timerLeftS(): number | null {
    if (this.timerEndsAt === null) return null;
    return Math.max(0, Math.ceil((this.timerEndsAt - this.d.now()) / 1000));
  }

  private syncClock(): void {
    this.pings = 0;
    this.bestRtt = Infinity;
    this.ping();
  }
  private ping(): void {
    this.transport?.sendBytes(MSG.ping, encodePing(this.d.now()));
  }

  private onLeave(t: RoomTransport, code: number, reason?: string): void {
    if (this.transport !== t || this.disposed) return;
    this.transport = null;
    if (code === 4000)
      return this.set({
        kind: "closed",
        reason: reason === "teacher" || reason === "idle" ? reason : null,
      });
    if (code === 4001) return this.set({ kind: "replaced" });
    if (code === 4003) return this.set({ kind: "rejected", code: "FE-302" });
    // 의도하지 않은 끊김 — 20초 창 안에서 5초마다
    const now = this.d.now();
    this.set({ kind: "reconnecting", deadline: now + RECONNECT_WINDOW_MS, attempts: 0 });
    this.timer = this.d.setTimeout(() => void this.attempt(), 0);
  }

  /** 내 기계 몸체 자세(transforms 에서 잘라) — 내 기계가 없으면 null */
  private mine(t: Float32Array): Float32Array | null {
    const me = this.meta?.machines.find((m) => m.machine === this.meta?.me.machine);
    return me ? t.slice(me.bodyStart * 7, (me.bodyStart + me.bodyCount) * 7) : null;
  }

  private loadPredictor(): void {
    const meta = this.meta;
    if (!this.d.predictor || !meta || meta.me.machine === null || this.predictorLoading) return;
    if (!predictable(meta.mode)) return;
    const key = modeKey(meta);
    let stale = false;
    this.predictorLoading = true;
    this.d
      .predictor(meta)
      .then((p) => {
        // 만드는 사이 모드가 바뀌었으면 버리고 새 모드로 다시
        stale = !!this.meta && modeKey(this.meta) !== key;
        if (!p || this.disposed || stale) return p?.dispose();
        const last = this.history[this.history.length - 1];
        if (last) p.sync(last.tick, this.mine(last.t)!);
        this.predictor = p;
        this.predictorMode = key;
      })
      .catch(() => {})
      .finally(() => {
        this.predictorLoading = false;
        if (stale && !this.disposed) this.loadPredictor();
      });
  }

  private dropPredictor(): void {
    this.predictor?.dispose();
    this.predictor = null;
    this.predictorMode = null;
  }

  /** 예측 이름표 = 서버 틱 추정 + 앞섬(RTT/2 + 여유) — 내 입력이 서버에서 반영될 틱 */
  private predictLabel(): number | null {
    const est = this.serverTick();
    if (est === null) return null;
    const lead = Number.isFinite(this.bestRtt) ? Math.ceil(this.bestRtt / 2 / TICK_MS) + 2 : 4;
    return est + Math.max(2, Math.min(30, lead));
  }

  get corrections(): number {
    return this.predictor?.corrections ?? 0;
  }

  /** 서버 틱 추정(동기 전이면 null) */
  serverTick(): number | null {
    return this.offset === null ? null : Math.floor(this.d.now() / TICK_MS + this.offset);
  }

  /** 리모컨 — 내 기계가 있을 때만(바뀔 때만 · 속도는 useRemote 가 maxHz 로 줄인다) */
  sendRemote(remote: RemoteInput): void {
    const me = this.meta?.me.machine;
    if (me === null || me === undefined || !this.transport || this.state.kind !== "live") return;
    // 분리(관전) 중에는 보내지 않는다(서버도 버린다)
    if (this.isolated) return;
    // 예측 중이면 로컬에 곧바로(다음 이름표부터) — 그 이름표를 입력 틱으로 보낸다
    const tick = this.predictor ? this.predictor.input(remote) : (this.serverTick() ?? 0);
    this.lastInputTick = tick;
    this.transport.sendBytes(MSG.input, encodeInput({ machine: me, tick, remote }));
  }

  /** 그릴 자세 — 서버 틱 추정 − 6틱에서 앞뒤 스냅샷 보간 */
  frame(): {
    tick: number;
    transforms: Float32Array;
    /** 원리 보기를 켰을 때만(SR-PHY-07 · 서버 값) */
    principle?: readonly PrincipleMachineLike[];
  } | null {
    const h = this.history;
    if (!h.length) return null;
    const est = this.serverTick();
    const target = est === null ? h[h.length - 1]!.tick : est - this.interpTicks;
    let a = h[0]!;
    let b = h[0]!;
    for (const s of h) {
      if (s.tick <= target) a = s;
      if (s.tick >= target) {
        b = s;
        break;
      }
      b = s;
    }
    const span = b.tick - a.tick;
    const u = span > 0 ? Math.min(1, Math.max(0, (target - a.tick) / span)) : 1;
    if (this.out.frame.length !== a.t.length) this.out.frame = new Float32Array(a.t.length);
    lerpTransforms(a.t, b.t, u, this.out.frame);
    // 내 기계는 예측 자세로(다른 기계는 보간만 — SR-NET-03)
    const p = this.predictor;
    const label = this.predictLabel();
    const me = this.meta?.machines.find((m) => m.machine === this.meta?.me.machine);
    if (p && label !== null && me) {
      p.advanceTo(label);
      this.out.frame.set(p.render(new Float32Array(me.bodyCount * 7)), me.bodyStart * 7);
    }
    const tick = Math.max(0, Math.floor(target));
    const pr = this.principleAt(target);
    if (!pr) return { tick, transforms: this.out.frame };
    return { tick, transforms: this.out.frame, principle: this.alignPrinciple(pr) };
  }

  /**
   * 원리 값을 그린 자세에 맞춘다 — 서버 값은 그 틱의 권위 자세 기준이라, 그 기계를 지금 그린 차체 자리(보간 · 내 기계는 예측)와의
   * 차이만큼 옮긴다(회전은 무시 — 몇 틱 차이). 그 틱의 받은 상태가 없으면 그대로.
   */
  private alignPrinciple(pr: {
    tick: number;
    machine: number;
    machines: PrincipleMachineLike[];
  }): PrincipleMachineLike[] {
    const m = this.meta?.machines.find((x) => x.machine === pr.machine);
    const at = this.history.find((h) => h.tick === pr.tick);
    if (!m || !at) return pr.machines;
    const o = m.bodyStart * 7;
    const f = this.out.frame;
    const d = [f[o]! - at.t[o]!, f[o + 1]! - at.t[o + 1]!, f[o + 2]! - at.t[o + 2]!] as const;
    if (!d[0] && !d[1] && !d[2]) return pr.machines;
    type V = readonly [number, number, number];
    const mv = (v: V): V => [v[0] + d[0], v[1] + d[1], v[2] + d[2]];
    return pr.machines.map((x) => ({
      ...x,
      com: x.com ? mv(x.com) : null,
      joints: x.joints.map((j) => ({ ...j, at: mv(j.at) })),
      wheels: x.wheels.map((w) => ({ ...w, at: mv(w.at) })),
      contacts: x.contacts.map((c) => ({ ...c, at: mv(c.at) })),
    }));
  }

  dispose(): void {
    this.disposed = true;
    this.generation++;
    if (this.timer !== null) this.d.clearTimeout(this.timer);
    const t = this.transport;
    this.transport = null;
    void t?.leave();
    this.dropPredictor();
    this.listeners.clear();
    this.eventListeners.clear();
  }
}
